/**
 * Handles the scrolling functionality for the news bar component
 */
export class NewsBar {
    private slider: HTMLElement | null;

    constructor() {
        this.slider = document.querySelector(".news-slider");
    }

    /**
     * Scrolls the news bar by the specified amount
     * @param amount The amount to scroll (positive for right, negative for left)
     */
    public scroll(amount: number): void {
        if (!this.slider) return;

        // Check if smooth scrolling is supported
        if ("scrollBehavior" in document.documentElement.style) {
            this.slider.scrollBy({ left: amount, behavior: "smooth" });
        } else {
            // Fallback for browsers that don't support smooth scrolling
            this.animateScroll(amount);
        }
    }

    /**
     * Animates the scroll for browsers that don't support smooth scrolling
     * @param amount The amount to scroll
     */
    private animateScroll(amount: number): void {
        if (!this.slider) return;

        const start = this.slider.scrollLeft;
        const end = start + amount;
        const duration = 300;
        const startTime = performance.now();

        const animate = (currentTime: number): void => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Easing function for smooth animation
            const easeInOutQuad = (t: number): number =>
                t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

            const current = start + (end - start) * easeInOutQuad(progress);

            if (this.slider) {
                this.slider.scrollLeft = current;
            }

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }
}

// Initialize the news bar functionality
const newsBar = new NewsBar();

// Expose the scroll function globally for the onclick handlers
(window as any).scrollNewsBar = (amount: number) => newsBar.scroll(amount);
